import { useRef, useState } from "react";
import {
  useListContractDocuments,
  useCreateContractDocument,
  useDeleteContractDocument,
  requestUploadUrl,
  getListContractDocumentsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DemoExportGate } from "@/components/DemoExportGate";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { FileText, Upload, Download, Trash2, Loader2, FileIcon, Archive } from "lucide-react";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
];
const MAX_BYTES = 25 * 1024 * 1024;

const LABEL_OPTIONS: { value: "executed_contract" | "amendment" | "addendum"; display: string }[] = [
  { value: "executed_contract", display: "Executed Contract" },
  { value: "amendment", display: "Amendment" },
  { value: "addendum", display: "Addendum" },
];

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function labelDisplay(label: string) {
  return LABEL_OPTIONS.find((o) => o.value === label)?.display ?? label;
}

function labelBadgeClasses(label: string) {
  switch (label) {
    case "executed_contract":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "amendment":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "addendum":
      return "bg-purple-50 text-purple-700 border-purple-200";
    default:
      return "";
  }
}

export function ContractDocuments({ contractId }: { contractId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState<"executed_contract" | "amendment" | "addendum">("executed_contract");
  const [uploading, setUploading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const { data: documents = [], isLoading } = useListContractDocuments(contractId, {
    query: { enabled: !!contractId, queryKey: getListContractDocumentsQueryKey(contractId) },
  });

  const createDoc = useCreateContractDocument();
  const deleteDoc = useDeleteContractDocument();

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getListContractDocumentsQueryKey(contractId) });

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({
        title: "Unsupported file type",
        description: `${file.name} is not a supported type. Allowed: PDF, DOC, DOCX, PNG, JPG, GIF, WEBP.`,
        variant: "destructive",
      });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast({
        title: "File too large",
        description: `${file.name} is ${formatBytes(file.size)}; maximum is ${formatBytes(MAX_BYTES)}.`,
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const presigned = await requestUploadUrl({
        name: file.name,
        size: file.size,
        contentType: file.type,
      });

      const putHeaders: Record<string, string> = {
        "Content-Type":
          (presigned as { requiredHeaders?: Record<string, string> }).requiredHeaders?.[
            "Content-Type"
          ] ?? file.type,
      };
      const putResp = await fetch(presigned.uploadURL, {
        method: "PUT",
        headers: putHeaders,
        body: file,
      });
      if (!putResp.ok) throw new Error(`Upload failed: ${putResp.status}`);

      await createDoc.mutateAsync({
        contractId,
        data: {
          label,
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          objectPath: presigned.objectPath,
        },
      });

      toast({ title: "Uploaded", description: `${file.name} attached as ${labelDisplay(label)}.` });
      refresh();
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number, fileName: string) => {
    try {
      await deleteDoc.mutateAsync({ id });
      toast({ title: "Deleted", description: `${fileName} was removed.` });
      refresh();
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingZip(true);
    try {
      const resp = await fetch(`/api/contracts/${contractId}/documents/zip`, {
        credentials: "include",
      });
      if (!resp.ok) {
        let msg = `Download failed: ${resp.status}`;
        try {
          const data = await resp.json();
          if (data?.error) msg = data.error;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contract-${contractId}-documents.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: "Download started", description: "Your ZIP archive has been downloaded." });
    } catch (err) {
      toast({
        title: "Download failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setDownloadingZip(false);
    }
  };

  const grouped = LABEL_OPTIONS.map((opt) => ({
    ...opt,
    docs: documents.filter((d) => d.label === opt.value),
  }));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" /> Documents
            </CardTitle>
            <CardDescription>
              Attach the executed contract PDF and any amendments or addenda. Max {formatBytes(MAX_BYTES)} per file.
            </CardDescription>
          </div>
          {documents.length > 0 && (
            <DemoExportGate label="Download all" size="sm" variant="outline">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownloadAll}
                disabled={downloadingZip}
              >
                {downloadingZip ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing…</>
                ) : (
                  <><Archive className="h-4 w-4 mr-2" /> Download all</>
                )}
              </Button>
            </DemoExportGate>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end p-4 rounded-md border bg-muted/30">
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">Document type</label>
            <Select value={label} onValueChange={(v) => setLabel(v as typeof label)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LABEL_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.display}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={ACCEPTED_TYPES.join(",")}
            onChange={handleFileSelected}
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="sm:w-auto"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Upload File</>
            )}
          </Button>
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading documents…</div>
        ) : documents.length === 0 ? (
          <div className="text-sm text-muted-foreground italic text-center py-6">
            No documents attached yet.
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map((group) =>
              group.docs.length === 0 ? null : (
                <div key={group.value}>
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-sm font-semibold">{group.display}</h4>
                    <Badge variant="outline" className={labelBadgeClasses(group.value)}>
                      {group.docs.length}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {group.docs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center gap-3 p-3 rounded-md border bg-background"
                      >
                        <FileIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate" title={doc.fileName}>{doc.fileName}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatBytes(doc.sizeBytes)} • Uploaded {format(new Date(doc.uploadedAt), "MMM d, yyyy 'at' h:mm a")}
                            {doc.uploadedBy ? ` • by ${doc.uploadedBy}` : ""}
                          </div>
                        </div>
                        <Button asChild variant="outline" size="sm">
                          <a href={doc.downloadUrl} target="_blank" rel="noopener noreferrer" download={doc.fileName}>
                            <Download className="h-4 w-4 mr-1" /> Download
                          </a>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {doc.fileName}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes the file from storage and cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(doc.id, doc.fileName)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
